import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RefreshCw, Plus, Pencil, Trash2, PauseCircle, PlayCircle, XCircle, Repeat, DollarSign, ListChecks } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CADENCES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Semi-annual" },
  { value: "annual", label: "Annual" },
];

const KINDS = [
  { value: "service", label: "Service" },
  { value: "good", label: "Good" },
  { value: "bundle", label: "Bundle" },
];

const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-300",
  trial: "bg-blue-100 text-blue-700 border-blue-300",
  paused: "bg-amber-100 text-amber-700 border-amber-300",
  cancelled: "bg-muted text-muted-foreground",
  expired: "bg-rose-100 text-rose-700 border-rose-300",
};

const blankPlan = () => ({
  name: "", description: "", kind: "service", service_id: null, good_id: null,
  bundle_items: [], price: 0, cadence: "monthly", billing_day_of_month: 1,
  trial_days: 0, auto_renew_default: true, is_active: true,
});

export default function Memberships() {
  const [tab, setTab] = useState("subs");
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [goods, setGoods] = useState([]);
  const [summary, setSummary] = useState(null);
  const [planDialog, setPlanDialog] = useState(null);
  const [subscribeDialog, setSubscribeDialog] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null);
  const [filterStatus, setFilterStatus] = useState("active,trial,paused");

  const load = async () => {
    try {
      const [p, s, c, sv, gd, sm] = await Promise.all([
        axios.get(`${API}/membership-plans?include_inactive=true`),
        axios.get(`${API}/memberships`),
        axios.get(`${API}/clients`).catch(() => ({ data: [] })),
        axios.get(`${API}/services`).catch(() => ({ data: [] })),
        axios.get(`${API}/goods`).catch(() => ({ data: [] })),
        axios.get(`${API}/memberships/summary`),
      ]);
      setPlans(p.data || []);
      setSubs(s.data || []);
      setClients(c.data?.items || c.data || []);
      setServices(sv.data?.items || sv.data || []);
      setGoods(gd.data?.items || gd.data || []);
      setSummary(sm.data);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filteredSubs = useMemo(() => {
    if (!filterStatus) return subs;
    const set = new Set(filterStatus.split(",").map((s) => s.trim()));
    return subs.filter((s) => set.has(s.status));
  }, [subs, filterStatus]);

  const savePlan = async () => {
    try {
      if (!planDialog.name) { toast.error("Name required"); return; }
      if (planDialog.id) await axios.put(`${API}/membership-plans/${planDialog.id}`, planDialog);
      else await axios.post(`${API}/membership-plans`, planDialog);
      toast.success("Plan saved");
      setPlanDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm("Deactivate plan?")) return;
    try { await axios.delete(`${API}/membership-plans/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const doSubscribe = async () => {
    if (!subscribeDialog.plan_id || !subscribeDialog.client_id) { toast.error("Pick plan + client"); return; }
    try {
      await axios.post(`${API}/memberships`, subscribeDialog);
      toast.success("Subscribed");
      setSubscribeDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doAction = async (sub, action, payload = {}) => {
    try {
      await axios.post(`${API}/memberships/${sub.id}/${action}`, payload);
      toast.success(`${action} success`);
      setStatusDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="memberships-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><Repeat className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Memberships</h1>
          <p className="text-muted-foreground">Recurring service & good subscriptions. Internal tracking only — live billing remains POS-upgrade territory.</p>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Active subs</p><p className="text-2xl font-bold">{summary.active_count}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">MRR</p><p className="text-2xl font-bold text-emerald-700">${summary.mrr.toFixed(2)}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Renewing in 30d</p><p className="text-2xl font-bold text-amber-700">{summary.expiring_within_30_days}</p></CardContent></Card>
          <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Plans</p><p className="text-2xl font-bold">{plans.filter((p) => p.is_active).length}</p></CardContent></Card>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="subs" data-testid="tab-subs"><ListChecks className="h-4 w-4 mr-1" /> Subscriptions</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans"><DollarSign className="h-4 w-4 mr-1" /> Plans</TabsTrigger>
        </TabsList>

        {/* SUBS */}
        <TabsContent value="subs">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div>
                <CardTitle>{filteredSubs.length} subscription{filteredSubs.length === 1 ? "" : "s"}</CardTitle>
                <CardDescription>Sorted by next billing date.</CardDescription>
              </div>
              <div className="flex gap-2 items-center">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active,trial,paused">Active / Trial / Paused</SelectItem>
                    <SelectItem value="active">Active only</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setSubscribeDialog({ plan_id: "", client_id: "", custom_price: null, notes: "" })} data-testid="new-sub-btn"><Plus className="h-4 w-4 mr-1" /> Subscribe</Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Client</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead>
                  <TableHead>Cadence</TableHead><TableHead>Price</TableHead><TableHead>Next billing</TableHead>
                  <TableHead>Renewals</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {filteredSubs.map((s) => (
                    <TableRow key={s.id} data-testid={`sub-row-${s.id}`}>
                      <TableCell className="font-medium">{s.client_name}</TableCell>
                      <TableCell>{s.plan_name}</TableCell>
                      <TableCell><Badge className={STATUS_COLOR[s.status] || ""} variant="outline">{s.status}</Badge></TableCell>
                      <TableCell className="text-xs">{s.plan_cadence}</TableCell>
                      <TableCell>${s.effective_price.toFixed(2)}{s.custom_price !== null && <span className="text-xs text-muted-foreground"> (custom)</span>}</TableCell>
                      <TableCell className="text-xs">{s.next_billing_date}</TableCell>
                      <TableCell>{s.renewal_count}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {s.status === "active" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => doAction(s, "renew")} data-testid={`renew-${s.id}`}><RefreshCw className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setStatusDialog({ sub: s, action: "pause" })}><PauseCircle className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                        {s.status === "paused" && (
                          <Button size="sm" variant="ghost" onClick={() => doAction(s, "resume")} data-testid={`resume-${s.id}`}><PlayCircle className="h-3.5 w-3.5" /></Button>
                        )}
                        {s.status === "trial" && (
                          <Button size="sm" variant="ghost" onClick={() => doAction(s, "renew")} data-testid={`convert-${s.id}`}>Activate</Button>
                        )}
                        {(s.status === "active" || s.status === "trial" || s.status === "paused") && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setStatusDialog({ sub: s, action: "cancel" })} data-testid={`cancel-${s.id}`}><XCircle className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredSubs.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No subscriptions in this filter.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANS */}
        <TabsContent value="plans">
          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <div><CardTitle>{plans.length} plan{plans.length === 1 ? "" : "s"}</CardTitle><CardDescription>Templates that clients can subscribe to.</CardDescription></div>
              <Button onClick={() => setPlanDialog(blankPlan())} data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-1" /> New plan</Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Kind</TableHead><TableHead>Cadence</TableHead><TableHead>Price</TableHead><TableHead>Trial</TableHead><TableHead>Active</TableHead><TableHead className="text-right" />
                </TableRow></TableHeader>
                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id} className={!p.is_active ? "opacity-50" : ""} data-testid={`plan-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell><Badge variant="outline">{p.kind}</Badge></TableCell>
                      <TableCell>{p.cadence}</TableCell>
                      <TableCell>${p.price.toFixed(2)}</TableCell>
                      <TableCell>{p.trial_days ? `${p.trial_days} d` : "—"}</TableCell>
                      <TableCell>{p.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => setPlanDialog({ ...p })} data-testid={`edit-plan-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePlan(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {plans.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No plans yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Plan editor */}
      <Dialog open={!!planDialog} onOpenChange={(o) => !o && setPlanDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{planDialog?.id ? "Edit plan" : "New plan"}</DialogTitle><DialogDescription>Recurring offering — the subscription template.</DialogDescription></DialogHeader>
          {planDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={planDialog.name} onChange={(e) => setPlanDialog({ ...planDialog, name: e.target.value })} data-testid="plan-name-input" /></div>
                <div><Label>Price ($)</Label><Input type="number" step="0.01" value={planDialog.price} onChange={(e) => setPlanDialog({ ...planDialog, price: parseFloat(e.target.value || 0) })} data-testid="plan-price-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Kind</Label>
                  <Select value={planDialog.kind} onValueChange={(v) => setPlanDialog({ ...planDialog, kind: v, service_id: null, good_id: null, bundle_items: [] })}>
                    <SelectTrigger data-testid="plan-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cadence</Label>
                  <Select value={planDialog.cadence} onValueChange={(v) => setPlanDialog({ ...planDialog, cadence: v })}>
                    <SelectTrigger data-testid="plan-cadence-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CADENCES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Billing day (1-28)</Label>
                  <Input type="number" min="1" max="28" value={planDialog.billing_day_of_month} onChange={(e) => setPlanDialog({ ...planDialog, billing_day_of_month: parseInt(e.target.value || 1) })} />
                </div>
              </div>

              {planDialog.kind === "service" && (
                <div>
                  <Label>Service</Label>
                  <Select value={planDialog.service_id || ""} onValueChange={(v) => setPlanDialog({ ...planDialog, service_id: v })}>
                    <SelectTrigger data-testid="plan-service-select"><SelectValue placeholder="Pick service" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {planDialog.kind === "good" && (
                <div>
                  <Label>Good</Label>
                  <Select value={planDialog.good_id || ""} onValueChange={(v) => setPlanDialog({ ...planDialog, good_id: v })}>
                    <SelectTrigger data-testid="plan-good-select"><SelectValue placeholder="Pick good" /></SelectTrigger>
                    <SelectContent>{goods.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {planDialog.kind === "bundle" && (
                <div>
                  <Label>Bundle items (raw JSON)</Label>
                  <Textarea rows={4} className="font-mono text-xs"
                    value={JSON.stringify(planDialog.bundle_items || [], null, 2)}
                    onChange={(e) => {
                      try { setPlanDialog({ ...planDialog, bundle_items: JSON.parse(e.target.value) }); }
                      catch { /* invalid mid-typing */ }
                    }}
                    placeholder='[{"kind":"service","item_id":"abc","qty":2}]' />
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div><Label>Trial days</Label><Input type="number" min="0" value={planDialog.trial_days} onChange={(e) => setPlanDialog({ ...planDialog, trial_days: parseInt(e.target.value || 0) })} /></div>
                <label className="flex items-center gap-2 cursor-pointer pt-6"><Switch checked={planDialog.auto_renew_default} onCheckedChange={(v) => setPlanDialog({ ...planDialog, auto_renew_default: v })} data-testid="plan-auto-renew-switch" /> <span>Auto-renew default</span></label>
                <label className="flex items-center gap-2 cursor-pointer pt-6"><Switch checked={planDialog.is_active} onCheckedChange={(v) => setPlanDialog({ ...planDialog, is_active: v })} /> <span>Active</span></label>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={planDialog.description || ""} onChange={(e) => setPlanDialog({ ...planDialog, description: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialog(null)}>Cancel</Button>
            <Button onClick={savePlan} data-testid="save-plan-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscribe dialog */}
      <Dialog open={!!subscribeDialog} onOpenChange={(o) => !o && setSubscribeDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Subscribe a client</DialogTitle><DialogDescription>Pick a plan + client. Optional custom price overrides plan price for this client.</DialogDescription></DialogHeader>
          {subscribeDialog && (
            <div className="space-y-3">
              <div>
                <Label>Plan</Label>
                <Select value={subscribeDialog.plan_id} onValueChange={(v) => setSubscribeDialog({ ...subscribeDialog, plan_id: v })}>
                  <SelectTrigger data-testid="subscribe-plan-select"><SelectValue placeholder="Pick plan" /></SelectTrigger>
                  <SelectContent>{plans.filter((p) => p.is_active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (${p.price}/{p.cadence})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client</Label>
                <Select value={subscribeDialog.client_id} onValueChange={(v) => setSubscribeDialog({ ...subscribeDialog, client_id: v })}>
                  <SelectTrigger data-testid="subscribe-client-select"><SelectValue placeholder="Pick client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start date (optional)</Label><Input type="date" value={subscribeDialog.start_date || ""} onChange={(e) => setSubscribeDialog({ ...subscribeDialog, start_date: e.target.value || null })} /></div>
                <div><Label>Custom price (optional)</Label><Input type="number" step="0.01" value={subscribeDialog.custom_price ?? ""} onChange={(e) => setSubscribeDialog({ ...subscribeDialog, custom_price: e.target.value === "" ? null : parseFloat(e.target.value) })} /></div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={subscribeDialog.notes || ""} onChange={(e) => setSubscribeDialog({ ...subscribeDialog, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscribeDialog(null)}>Cancel</Button>
            <Button onClick={doSubscribe} data-testid="confirm-subscribe-btn">Subscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status action dialog (pause / cancel reason) */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{statusDialog?.action === "pause" ? "Pause subscription" : "Cancel subscription"}</DialogTitle></DialogHeader>
          {statusDialog && (
            <div className="space-y-3">
              {statusDialog.action === "pause" && (
                <div><Label>Paused until</Label><Input type="date" value={statusDialog.paused_until || ""} onChange={(e) => setStatusDialog({ ...statusDialog, paused_until: e.target.value })} data-testid="paused-until-input" /></div>
              )}
              <div><Label>{statusDialog.action === "cancel" ? "Reason" : "Notes"}</Label><Textarea rows={2} value={statusDialog.reason || ""} onChange={(e) => setStatusDialog({ ...statusDialog, reason: e.target.value })} data-testid="status-reason-input" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Back</Button>
            <Button onClick={() => doAction(statusDialog.sub, statusDialog.action, statusDialog.action === "pause" ? { paused_until: statusDialog.paused_until } : { reason: statusDialog.reason })} data-testid="confirm-status-btn">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
